import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import './UserAdmin.css';
import * as api from './userAdminApi';
import { UaContext } from './UserAdminContext';
import { useUserAccess } from '../../context/UserAccessContext';
import Icon from './UaIcon';
import { Layer } from './UaOverlay';
import { EMPTY_ACT, EMPTY_FILTERS, copyText, fullName, inviteMessage, isoDay, nameFromEmail, plural } from './uaModel';
import UsersTab from './UsersTab';
import RolesTab from './RolesTab';
import ActivityTab from './ActivityTab';
import PersonPanel from './PersonPanel';
import { AddUserDialog, DeactivateDialog, InviteDialog, ReactivateDialog } from './UserDialogs';
import PageHeader, { TABS } from './UaHeader';

/** User Administration: Users, Roles and Activity log. IT Managers only (see UserAdminRoute). */
export default function UserAdmin({ tab = 'users' }) {
    const navigate = useNavigate();
    const { access, refresh: refreshAccess } = useUserAccess();
    const me = access ? access.email : '';

    const [catalog, setCatalog] = useState(null);
    const [data, setData] = useState(null);
    const [unmanaged, setUnmanaged] = useState(null);
    const [loadError, setLoadError] = useState('');
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [actFilters, setActFilters] = useState(EMPTY_ACT);
    const [sel, setSel] = useState(() => new Set());
    const [panel, setPanel] = useState(null);
    const [modal, setModal] = useState(null);
    const [toastState, setToastState] = useState(null);
    const [flash, setFlash] = useState(null);
    const toastTimer = useRef(null);

    const load = useCallback(async () => {
        setLoadError('');
        try {
            const [cat, state] = await Promise.all([api.getCatalog(), api.getState()]);
            setCatalog(cat);
            setData({ people: state.people, roles: state.roles, log: state.log });
            setUnmanaged(state.unmanaged);
        } catch (err) {
            setLoadError(err.message);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    /** `opts.undoToken` adds Undo; `opts.action` ({ label, onClick }) adds another button. */
    const toast = useCallback((content, opts = {}) => {
        clearTimeout(toastTimer.current);
        setToastState({ content, kind: opts.kind || 'ok', undoToken: opts.undoToken || null, action: opts.action || null, key: Date.now() });
        toastTimer.current = setTimeout(() => setToastState(null), opts.undoToken || opts.action ? 9000 : 5000);
    }, []);
    useEffect(() => () => clearTimeout(toastTimer.current), []);

    useEffect(() => {
        if (!flash) return undefined;
        const t = setTimeout(() => setFlash(null), 2600);
        return () => clearTimeout(t);
    }, [flash]);

    const applyState = useCallback((res) => {
        if (res && Array.isArray(res.people)) setData({ people: res.people, roles: res.roles, log: res.log });
    }, []);

    /**
     * Sends a change and shows the outcome. `success` is a message or a function of the response.
     * The toast offers Undo when the server returned a token and `opts.undo` (true, false or a
     * function of the response) allows it.
     */
    const run = useCallback(async (request, success, opts = {}) => {
        try {
            const res = await request;
            applyState(res);
            const message = typeof success === 'function' ? success(res) : success;
            const allowUndo = typeof opts.undo === 'function' ? opts.undo(res) : opts.undo !== false;
            if (message) toast(message, { undoToken: allowUndo ? res.undoToken : null, action: opts.action });
            return res;
        } catch (err) {
            toast(err.message, { kind: 'error' });
            // Lost the IT Manager role, or the session ended: re-check what this person may open.
            if (err.status === 401 || err.status === 403) refreshAccess();
            return null;
        }
    }, [applyState, toast, refreshAccess]);

    const undo = useCallback((token) => {
        setToastState(null);
        run(api.undo(token), 'Undone.', { undo: false });
    }, [run]);

    const goTab = useCallback((id) => {
        const t = TABS.find((x) => x.id === id);
        if (t) navigate(t.path);
        window.scrollTo(0, 0);
    }, [navigate]);

    const actions = useMemo(() => ({
        /**
         * Copies the invite message; if the browser won't allow it, shows the text to copy by hand.
         * `password` is passed only right after adding someone.
         */
        shareInvite: async (person, password) => {
            const ok = await copyText(inviteMessage(person, password));
            if (!ok) { setModal({ type: 'invite', id: person.id, password }); return; }
            await run(api.markInvited(person.id),
                <>Invite copied. Paste it into an email or Teams chat to <b>{fullName(person)}</b>.</>, { undo: false });
        },
        /** Downloads one sheet as an .xlsx file. `what` names the rows in the toast. */
        exportSheet: (sheet, base, what) => {
            const ws = XLSX.utils.aoa_to_sheet([sheet.head].concat(sheet.rows));
            ws['!cols'] = sheet.head.map((h, i) => ({
                wch: Math.min(60, sheet.rows.reduce((w, r) => Math.max(w, String(r[i] == null ? '' : r[i]).length), h.length) + 2),
            }));
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, sheet.name);
            const file = `bridge-${base}-${isoDay(new Date())}.xlsx`;
            XLSX.writeFile(wb, file);
            toast(<>Exported {plural(sheet.rows.length, what)} to <b>{file}</b>.</>);
        },
    }), [run, toast]);

    // People who sign in to Bridge but aren't set up here. Anyone set up since drops off.
    const unmanagedLeft = useMemo(() => {
        if (!unmanaged || !data) return [];
        return unmanaged
            .filter((u) => !data.people.some((p) => p.email === u.email))
            .map((u) => ({ email: u.email, ...nameFromEmail(u.email, u.name), title: u.title || '', dept: u.dept || '' }));
    }, [unmanaged, data]);

    const meId = useMemo(() => {
        const p = data && data.people.find((x) => x.email === me);
        return p ? p.id : null;
    }, [data, me]);

    const ctx = useMemo(() => ({
        catalog, data, me, meId, run, toast, actions,
        filters, setFilters, actFilters, setActFilters, sel, setSel, flash, setFlash,
        unmanaged: unmanagedLeft,
        openPanel: (id) => { setModal(null); setPanel({ id, key: Date.now() }); },
        closePanel: () => setPanel(null),
        openModal: setModal,
        closeModal: () => setModal(null),
        goTab,
    }), [catalog, data, me, meId, run, toast, actions, filters, actFilters, sel, flash, unmanagedLeft, goTab]);

    const current = TABS.find((t) => t.id === tab) || TABS[0];
    let body;
    if (loadError || !data || !catalog) {
        body = (
            <>
                <PageHeader tab={current.id} title={current.label} sub="Decide who can sign in to Bridge and what each person can open." />
                {loadError ? (
                    <section className="ua-card ua-state">
                        <span className="ua-state-ico"><Icon name="alert" size={24} /></span>
                        <b>User Administration didn’t load</b>
                        <span>{loadError}</span>
                        <button type="button" className="ua-btn" onClick={load}><Icon name="refresh" size={15} />Try again</button>
                    </section>
                ) : (
                    <section className="ua-card ua-state" aria-busy="true"><span className="ua-spinner" /><span>Loading users and roles…</span></section>
                )}
            </>
        );
    } else if (tab === 'roles') body = <RolesTab />;
    else if (tab === 'activity') body = <ActivityTab />;
    else body = <UsersTab />;

    return (
        <UaContext.Provider value={ctx}>
            <div className="ua ua-page">{body}</div>

            {data && catalog && panel ? <PersonPanel key={panel.key} id={panel.id} /> : null}
            {data && catalog && modal && (modal.type === 'add' || modal.type === 'setup') ? <AddUserDialog setup={modal.type === 'setup' ? modal.person : null} /> : null}
            {data && catalog && modal && modal.type === 'deact' ? <DeactivateDialog ids={modal.ids} /> : null}
            {data && catalog && modal && modal.type === 'react' ? <ReactivateDialog id={modal.id} /> : null}
            {data && catalog && modal && modal.type === 'invite' ? <InviteDialog id={modal.id} password={modal.password} /> : null}

            {toastState ? (
                <Layer>
                    <div key={toastState.key} className={`ua-toast${toastState.kind === 'error' ? ' ua-toast-error' : ''}`} role="status">
                        {toastState.kind === 'error' ? <Icon name="alert" size={16} /> : null}
                        <span>{toastState.content}</span>
                        {toastState.action ? (
                            <button type="button" className="ua-toast-act" onClick={() => { setToastState(null); toastState.action.onClick(); }}>{toastState.action.label}</button>
                        ) : null}
                        {toastState.undoToken ? (
                            <button type="button" className="ua-toast-act" onClick={() => undo(toastState.undoToken)}>Undo</button>
                        ) : null}
                        <button type="button" className="ua-toast-x" onClick={() => setToastState(null)} aria-label="Dismiss"><Icon name="x" size={14} /></button>
                    </div>
                </Layer>
            ) : null}
        </UaContext.Provider>
    );
}
